/**
 * Dynamic Search Placeholder - slide-out / slide-in cycling.
 * Per-wrapper state via WeakMap avoids races when multiple overlays exist
 * (header + predictive-search modal).
 */
(function() {
  'use strict';

  const searchTerms = [
    'AirPods',
    'MacBook',
    'iPhone',
    'iPad',
    'Apple Watch',
    'iMac',
    'Mac Mini',
    'AirPods Pro',
    'MacBook Air'
  ];

  const SLIDE_OUT_MS = 220;  // must match .slide-out animation duration
  const SLIDE_IN_MS = 320;   // must match .slide-in animation duration
  const CYCLE_MS = 3000;

  const states = new WeakMap();
  let currentIndex = 0;
  let cycleTimer = null;
  let cycleOverlays = null;
  let visibleCount = 0; // How many overlays are currently on-screen.

  function animateText(wrapper, newText) {
    const span = wrapper.querySelector('.search-placeholder-dynamic .search-placeholder-text');
    if (!span) return;

    let state = states.get(wrapper);
    if (!state) {
      state = { slideOutTimer: null, slideInTimer: null };
      states.set(wrapper, state);
    }

    // Cancel any in-flight timers - e.g. user was mid-animation on last tick.
    if (state.slideOutTimer) { clearTimeout(state.slideOutTimer); state.slideOutTimer = null; }
    if (state.slideInTimer) { clearTimeout(state.slideInTimer); state.slideInTimer = null; }

    // Phase 1 - slide current text out.
    span.classList.remove('slide-in');
    span.classList.add('slide-out');

    state.slideOutTimer = setTimeout(() => {
      state.slideOutTimer = null;
      // Swap text while out-of-view, then slide in.
      span.textContent = newText;
      span.classList.remove('slide-out');
      // Double rAF lets the browser commit the class removal before re-adding,
      // restarting the slide-in animation without a forced layout reflow.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          span.classList.add('slide-in');
          state.slideInTimer = setTimeout(() => {
            state.slideInTimer = null;
            span.classList.remove('slide-in');
          }, SLIDE_IN_MS);
        });
      });
    }, SLIDE_OUT_MS);
  }

  function scheduleNextTick() {
    if (cycleTimer) { clearTimeout(cycleTimer); cycleTimer = null; }
    if (!cycleOverlays || visibleCount === 0) return;
    cycleTimer = setTimeout(tickIfVisible, CYCLE_MS);
  }

  function tickIfVisible() {
    cycleTimer = null;
    // Skip the text swap entirely when the tab is backgrounded or the search
    // bar is off-screen - saves the setTimeout work and layout churn.
    if (document.hidden || visibleCount === 0 || !cycleOverlays) {
      scheduleNextTick();
      return;
    }
    currentIndex = (currentIndex + 1) % searchTerms.length;
    const newText = searchTerms[currentIndex];
    cycleOverlays.forEach((overlay) => animateText(overlay, newText));
    scheduleNextTick();
  }

  function init() {
    const overlays = document.querySelectorAll('.search-placeholder-wrapper');
    if (overlays.length === 0) return;
    cycleOverlays = Array.from(overlays);

    if ('IntersectionObserver' in window) {
      // Pause cycling whenever none of the search bars are visible.
      const visibility = new WeakMap();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const was = visibility.get(entry.target) === true;
          const now = entry.isIntersecting;
          if (was === now) return;
          visibility.set(entry.target, now);
          visibleCount += now ? 1 : -1;
          if (visibleCount < 0) visibleCount = 0;
        });
        // Re-arm when visibility flips from 0 to >0 (e.g. user scrolled the header back into view).
        if (visibleCount > 0 && !cycleTimer) scheduleNextTick();
      });
      cycleOverlays.forEach((el) => io.observe(el));
    } else {
      // No IO support: assume always visible.
      visibleCount = cycleOverlays.length;
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && visibleCount > 0 && !cycleTimer) scheduleNextTick();
    });

    scheduleNextTick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
